import React, { useEffect, useMemo, useState, useRef } from 'react';
import { getOrderColumns } from './api';
import { useOutletContext } from 'react-router-dom';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';

import {
  ErrorMessage,
  MessageContainer,
  TableContainer,
  StatusBadge,
} from './style';
import { formatCurrency, formatDate, getTotalWeight, getStatusText } from './utils';
import { fetchPaginatedOrders, ITEMS_PER_PAGE } from './api';
import { SteelOrder } from './types';
import { Filter } from '../../../../components/SubHeaderFilter/types';
import ActionsDropdown from './ActionsDropdown';
import { OverflowCell } from '../../../../components/OverflowCell';
import {
  StandardDataTable,
  useColumnsState,
  StandardColumn,
} from '../../../../components/StandardPage';

const steelOrdersScreenKey = 'steel_orders';

interface OutletContextType {
  refresh: boolean;
  appliedFilters: Filter[];
  search: string;
}

const SteelOrdersTable: React.FC = (): JSX.Element => {
  const { refresh, appliedFilters, search } = useOutletContext<OutletContextType>();
  const queryClient = useQueryClient();
  const [isCustomOrder, setIsCustomOrder] = useState(false);
  const [customOrderedData, setCustomOrderedData] = useState<SteelOrder[] | null>(null);
  const [duplicatedOrderIds, setDuplicatedOrderIds] = useState<number[]>([]);
  const prevFlatDataRef = useRef<SteelOrder[]>([]);

  useEffect(() => {
    setIsCustomOrder(false);
    setCustomOrderedData(null);
    queryClient.invalidateQueries({ queryKey: ['steelOrders'], refetchType: 'all' });
  }, [refresh, queryClient]);

  const {
    data: queryData,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isPending,
    isError,
  } = useInfiniteQuery<any, Error, any>({
    queryKey: ['steelOrders', appliedFilters, search],
    queryFn: async ({ pageParam = 1 }: { pageParam: unknown }) => {
      return fetchPaginatedOrders({
        pageParam: pageParam as number,
        filter: appliedFilters,
        search: search,
      });
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      if (!lastPage || !lastPage.data) return undefined;
      const totalFetched = lastPage.page * ITEMS_PER_PAGE;
      if (totalFetched < lastPage.total) {
        return lastPage.page + 1;
      }
      return undefined;
    },
    select: (queryData) => {
      return queryData;
    },
  });

  const [allColumns, setAllColumns] = useState<{ key: string; label: string }[]>([]);

  useEffect(() => {
    getOrderColumns().then(setAllColumns).catch(() => setAllColumns([]));
  }, []);

  const defaultColumns: StandardColumn<SteelOrder>[] = useMemo(() => [
    {
      id: 'id',
      label: 'ID',
      accessor: (order) => order.id,
      cell: (order) => {
        const isDuplicated = duplicatedOrderIds.includes(order.id);
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>{order.id}</span>
            {isDuplicated && (
              <span style={{
                backgroundColor: '#ff6b6b',
                color: 'white',
                padding: '2px 6px',
                borderRadius: '4px',
                fontSize: '10px',
                fontWeight: 'bold',
                textTransform: 'uppercase'
              }}>
                DUPLICADO
              </span>
            )}
          </div>
        );
      },
    },
    {
      id: 'client',
      label: 'Cliente',
      accessor: (order) => order.client ?? '-',
      cell: (order) => <OverflowCell text={String(order.client ?? '-')} maxWidth={180} />,
    },
    {
      id: 'orderDate',
      label: 'Data do Pedido',
      accessor: (order) => order.orderDate ?? '',
      cell: (order) => <>{formatDate(order.orderDate as string)}</>,
      exportValue: (order) => formatDate(order.orderDate as string),
    },
    {
      id: 'deliveryDate',
      label: 'Previsão de Entrega',
      accessor: (order) => order.deliveryDate ?? '',
      cell: (order) => <>{formatDate(order.deliveryDate as string)}</>,
      exportValue: (order) => formatDate(order.deliveryDate as string),
    },
    {
      id: 'items',
      label: 'Peso Total',
      accessor: (order) => getTotalWeight(order.items as any[]),
    },
    {
      id: 'total',
      label: 'Valor Total',
      accessor: (order) => order.total,
      cell: (order) => <>{formatCurrency(order.total as number)}</>,
      exportValue: (order) => formatCurrency(order.total as number),
    },
    {
      id: 'status',
      label: 'Status',
      accessor: (order) => getStatusText(order.status as boolean | null | undefined),
      disableTooltip: true,
      cell: (order) => (
        <StatusBadge status={order.status as boolean | null | undefined}>
          {getStatusText(order.status as boolean | null | undefined)}
        </StatusBadge>
      ),
    },
  ], [duplicatedOrderIds]);

  const extraColumnDefs: StandardColumn<SteelOrder>[] = useMemo(() =>
    allColumns
      .filter(col =>
        col.key !== 'actions' &&
        !defaultColumns.some(def => def.id === col.key)
      )
      .map(col => ({
        id: col.key,
        label: col.label,
        defaultHidden: true,
        accessor: (order: SteelOrder) => String((order as any)[col.key] ?? ''),
        cell: (order: SteelOrder) => (
          <OverflowCell text={String((order as any)[col.key] ?? '')} maxWidth={160} />
        ),
      })),
    [allColumns, defaultColumns]
  );

  const columns = useMemo<StandardColumn<SteelOrder>[]>(
    () => [...defaultColumns, ...extraColumnDefs],
    [defaultColumns, extraColumnDefs]
  );

  const { columnsState, handleColumnsChange, handleRestoreColumns } =
    useColumnsState(steelOrdersScreenKey, columns);

  const flatData = useMemo(() => {
    if (Array.isArray(queryData)) {
      return queryData;
    }

    if (queryData && queryData.pages && Array.isArray(queryData.pages)) {
      return queryData.pages.flatMap((page: any) => page.data ?? []);
    }

    return [];
  }, [queryData]);

  useEffect(() => {
    const lastDuplicatedFrom = localStorage.getItem('lastDuplicatedFrom');

    if (lastDuplicatedFrom && flatData.length > 0) {
      const originalOrderId = parseInt(lastDuplicatedFrom, 10);

      const originalOrder = flatData.find((o: SteelOrder) => o.id === originalOrderId);

      if (!originalOrder) {
        localStorage.removeItem('lastDuplicatedFrom');
        prevFlatDataRef.current = flatData;
        return;
      }

      const baseData = customOrderedData || prevFlatDataRef.current;
      const baseDataIds = new Set(baseData.map((o: SteelOrder) => o.id));

      const newDuplicatedOrder = flatData.find((order: SteelOrder) => {
        const notInBase = !baseDataIds.has(order.id);
        const notMarked = !duplicatedOrderIds.includes(order.id);
        const sameClient = order.client === originalOrder.client;
        const hasDate = order.orderDate && originalOrder.orderDate;
        const timeDiff = hasDate ? Math.abs(new Date(order.orderDate!).getTime() - new Date(originalOrder.orderDate!).getTime()) : Infinity;
        const closeTime = timeDiff < 60000;

        return notInBase && notMarked && sameClient && hasDate && closeTime;
      });

      if (newDuplicatedOrder) {
        let reorderedData: SteelOrder[];

        if (customOrderedData) {
          reorderedData = [...customOrderedData];

          const alreadyExists = reorderedData.some(o => o.id === newDuplicatedOrder.id);
          if (!alreadyExists) {
            reorderedData.push(newDuplicatedOrder);
          }
        } else {
          reorderedData = [...flatData];
        }

        const existingIndex = reorderedData.findIndex((order: SteelOrder) => order.id === newDuplicatedOrder.id);
        if (existingIndex !== -1) {
          reorderedData.splice(existingIndex, 1);
        }

        const originalIndex = reorderedData.findIndex((order: SteelOrder) => order.id === originalOrderId);

        if (originalIndex !== -1) {
          reorderedData.splice(originalIndex + 1, 0, newDuplicatedOrder);

          setCustomOrderedData(reorderedData);
          setIsCustomOrder(true);
          setDuplicatedOrderIds(prev => [...prev, newDuplicatedOrder.id]);

          localStorage.removeItem('lastDuplicatedFrom');
        }

        prevFlatDataRef.current = flatData;
        return;
      }

      if (flatData.length > prevFlatDataRef.current.length) {
        localStorage.removeItem('lastDuplicatedFrom');
      }

      prevFlatDataRef.current = flatData;
      return;
    }

    prevFlatDataRef.current = flatData;

  }, [flatData, customOrderedData, duplicatedOrderIds, isCustomOrder]);

  useEffect(() => {
    if (isCustomOrder && customOrderedData) {
      if (customOrderedData.length > flatData.length) {
        const flatDataIds = new Set(flatData.map((item: SteelOrder) => item.id));
        const filteredCustomData = customOrderedData.filter((item: SteelOrder) => flatDataIds.has(item.id));

        if (filteredCustomData.length < customOrderedData.length) {
          setCustomOrderedData(filteredCustomData);
        }
      }

      if (flatData.length > customOrderedData.length) {
        const customDataIds = new Set(customOrderedData.map((item: SteelOrder) => item.id));
        const newItems = flatData.filter((item: SteelOrder) => !customDataIds.has(item.id));

        if (newItems.length > 0) {
          const updatedData = [...customOrderedData, ...newItems];
          setCustomOrderedData(updatedData);
        }
      }
    }
  }, [flatData, isCustomOrder, customOrderedData]);

  const handleLoadMore = () => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  if (isPending) {
    return <MessageContainer>Carregando pedidos...</MessageContainer>;
  }

  if (isError) {
    return <ErrorMessage>Não foi possível carregar os pedidos. Erro: {error?.message}</ErrorMessage>;
  }

  return (
    <TableContainer>
      <StandardDataTable
        columns={columns}
        data={customOrderedData ?? flatData ?? []}
        columnsState={columnsState}
        onColumnsChange={handleColumnsChange}
        onRestoreColumns={handleRestoreColumns}
        totalCount={queryData && Array.isArray(queryData.pages) && queryData.pages.length > 0 ? queryData.pages[0].total : null}
        onLoadMore={handleLoadMore}
        hasMore={!!hasNextPage}
        isLoading={isFetchingNextPage}
        countLabel='pedidos'
        exportFileName='pedidos'
        emptyMessage='Nenhum pedido encontrado.'
        renderActions={(order) => <ActionsDropdown order={order} />}
      />
    </TableContainer>
  );
};

export default SteelOrdersTable;
